import React from "react";
import monaco from "../../../assets/images/monaco.jpg";
import logo from "../../../assets/logos/Logo.svg";

/**
 * Mise en page commune aux écrans d'authentification : visuel à gauche à partir
 * du grand écran, carte de formulaire à droite, pleine largeur sur mobile.
 */
export const AuthShell = ({
  eyebrow,
  headline,
  description,
  title,
  subtitle,
  children,
  footer,
}: {
  eyebrow: string;
  headline: string;
  description: string;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) => (
  <div className="flex min-h-screen w-full items-center justify-center bg-slate-100 p-4 sm:p-6">
    <div className="grid w-full max-w-5xl gap-5 lg:grid-cols-2">
      {/* Visuel — masqué sous lg */}
      <div className="relative hidden overflow-hidden rounded-2xl shadow-sm lg:block">
        <div
          aria-hidden="true"
          style={{ backgroundImage: `url(${monaco})` }}
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-slate-900/55"
        />
        <div className="relative flex h-full min-h-[30rem] flex-col justify-end p-8 text-white">
          <p className="text-[11px] font-bold tracking-wider text-white/70 uppercase">
            {eyebrow}
          </p>
          <h2 className="mt-2 text-2xl leading-tight font-bold">{headline}</h2>
          <p className="mt-2 max-w-sm font-Nunito text-sm leading-relaxed text-white/80">
            {description}
          </p>
        </div>
      </div>

      {/* Carte de formulaire */}
      <div className="flex flex-col justify-center rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <img
          src={logo}
          alt={import.meta.env.VITE_APP_TITLE}
          className="h-10 self-start"
        />

        {title ? (
          <h1 className="mt-6 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
            {title}
          </h1>
        ) : null}
        {subtitle ? (
          <p className="mt-1 font-Nunito text-sm text-slate-500">{subtitle}</p>
        ) : null}

        {children}

        {footer ? (
          <div className="mt-7 flex flex-wrap items-center justify-center gap-2 border-t border-slate-100 pt-5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  </div>
);

/**
 * Champ de formulaire d'authentification. `trailing` accueille par exemple le
 * bouton d'affichage du mot de passe.
 */
export const AuthField = ({
  label,
  type,
  value,
  onChange,
  required = false,
  autoComplete,
  trailing,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  autoComplete?: string;
  trailing?: React.ReactNode;
}) => (
  <label className="block">
    <span className="font-Nunito text-sm font-semibold text-slate-700">
      {label}
    </span>
    <span className="relative mt-1.5 block">
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        autoComplete={autoComplete}
        className={`h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 font-Nunito text-sm text-slate-900 transition-colors placeholder:text-slate-400 focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:outline-none ${
          trailing ? "pr-11" : ""
        }`}
      />
      {trailing}
    </span>
  </label>
);

/**
 * Bouton d'affichage / masquage du mot de passe, positionné dans le champ.
 */
export const PasswordToggle = ({
  visible,
  onToggle,
  icon,
}: {
  visible: boolean;
  onToggle: () => void;
  icon: string;
}) => (
  <button
    type="button"
    aria-label={visible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
    className="absolute top-1/2 right-2 inline-flex size-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100"
  >
    <img src={icon} alt="" aria-hidden="true" className="size-4.5" />
  </button>
);

export const AuthSubmit = ({
  isLoading = false,
  loadingLabel,
  children,
}: {
  isLoading?: boolean;
  loadingLabel?: string;
  children: React.ReactNode;
}) => (
  <button
    type="submit"
    disabled={isLoading}
    className="mt-1 inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-dca-blue font-Nunito text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60"
  >
    {isLoading && (
      <span
        aria-hidden="true"
        className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
      />
    )}
    {isLoading ? (loadingLabel ?? "Chargement...") : children}
  </button>
);

export const AuthFooterLink = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="cursor-pointer font-Nunito text-xs font-semibold text-slate-500 transition-colors hover:text-dca-blue"
  >
    {children}
  </button>
);

export const AuthCopyright = () => (
  <p className="font-Nunito text-xs text-slate-400">© 2026 by TRIONODE</p>
);
